import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectApi, ToastStateHandler } from '../../../api';
import { useContainer } from '../../useContainer';
import { useTranslation } from '../../useTranslation';
import { ProjectTotalEarningsDto } from '../../../commonTypes/acs';
import { setCachedEarnings, selectCachedEarnings } from '../../../store/slices/projects.slice';

const useHttpGetProjectTotalEarnings = (projectId: string, enabled: boolean = true) => {
    const projectApi: ProjectApi = useContainer(ProjectApi);
    const toastHandler = useContainer(ToastStateHandler);
    const dispatch = useDispatch();
    const cachedData = useSelector(selectCachedEarnings(projectId));
    const [isReady, setIsReady] = useState(false);
    const [data, setData] = useState<ProjectTotalEarningsDto | null>(cachedData || null);
    const { t } = useTranslation('newNotifications');

    const getEarnings = (id: string) => {
        if (!id || id.trim() === '') {
            setIsReady(true);
            return;
        }

        const { exec, abort } = projectApi.getProjectTotalEarnings({
            params: { projectId: id },
            onSuccess: res => {
                dispatch(setCachedEarnings({ projectId: id, data: res.body }));
                setData(res.body);
            },
            onError: error => {
                console.error('Error fetching project total earnings:', error);
                toastHandler.push('error', t('common.error'), t('common.somethingWentWrong'));
            },
            onFinally: () => setIsReady(true),
        });

        exec();
        return abort;
    };

    useEffect(() => {
        if (!enabled) {
            setIsReady(true);
            return;
        }

        if (cachedData) {
            setData(cachedData);
            setIsReady(true);
            return;
        }

        if (projectId && projectId.trim() !== '') {
            getEarnings(projectId);
        } else {
            setIsReady(true);
        }
    }, [projectId, enabled]);

    useEffect(() => {
        if (cachedData) {
            setData(cachedData);
        }
    }, [cachedData]);

    return { isReady, data, getEarnings };
};

export default useHttpGetProjectTotalEarnings;
